import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Box,
  Button,
  FormControlLabel,
  FormHelperText,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { IconChevronDown, IconWand } from '@tabler/icons-react';
// eslint-disable-next-line @typescript-eslint/no-explicit-any
import { Control, Controller } from 'react-hook-form';

import CustomSwitch from '@shared/components/forms/theme-elements/CustomSwitch';

interface TurnHandlingFieldsProps {
  // Both the add form (useFormContext) and the edit form (useForm) pass their
  // own control; the field names live under the `turnHandling` object.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  control: Control<any>;
  disabled?: boolean;
  collapsible?: boolean;
  onApplyDefaults?: () => void;
}

const parseNumber = (raw: string): number | undefined => (raw === '' ? undefined : Number(raw));

export const TurnHandlingFields: React.FC<TurnHandlingFieldsProps> = ({
  control,
  disabled = false,
  collapsible = true,
  onApplyDefaults,
}) => {
  const { t } = useTranslation();

  const numberField = (name: string, labelKey: string, step = '0.1') => (
    <Controller
      name={name}
      control={control}
      render={({ field }) => (
        <TextField
          fullWidth
          type="number"
          disabled={disabled}
          inputProps={{ min: 0, step }}
          label={t(labelKey)}
          helperText={t(`${labelKey}Help`)}
          value={field.value ?? ''}
          onChange={(e) => field.onChange(parseNumber(e.target.value))}
          variant="outlined"
        />
      )}
    />
  );

  const switchField = (name: string, labelKey: string) => (
    <Controller
      name={name}
      control={control}
      render={({ field }) => (
        <Box>
          <FormControlLabel
            control={
              <CustomSwitch
                sx={{ ml: '20px', mr: '14px' }}
                checked={!!field.value}
                disabled={disabled}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  field.onChange(e.target.checked)
                }
              />
            }
            label={t(labelKey)}
          />
          <FormHelperText sx={{ ml: '20px' }}>{t(`${labelKey}Help`)}</FormHelperText>
        </Box>
      )}
    />
  );

  const selectField = (name: string, labelKey: string, options: string[]) => (
    <Controller
      name={name}
      control={control}
      render={({ field }) => (
        <TextField
          select
          fullWidth
          disabled={disabled}
          label={t(labelKey)}
          helperText={t(`${labelKey}Help`)}
          value={field.value ?? ''}
          onChange={(e) => field.onChange(e.target.value === '' ? undefined : e.target.value)}
          variant="outlined">
          <MenuItem value="">{t('assistantsPage.modals.turnHandling.inherit')}</MenuItem>
          {options.map((opt) => (
            <MenuItem key={opt} value={opt}>
              {opt}
            </MenuItem>
          ))}
        </TextField>
      )}
    />
  );

  const body = (
    <>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        spacing={2}
        sx={{ mb: 2 }}>
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          {t('assistantsPage.modals.turnHandling.description')}
        </Typography>
        {onApplyDefaults && !disabled && (
          <Button
            size="small"
            variant="outlined"
            startIcon={<IconWand size={16} />}
            onClick={onApplyDefaults}
            sx={{ flexShrink: 0 }}>
            {t('assistantsPage.modals.applyDefaults')}
          </Button>
        )}
      </Stack>

      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
        {numberField(
          'turnHandling.minEndpointingDelay',
          'assistantsPage.modals.turnHandling.minEndpointingDelay',
        )}
        {numberField(
          'turnHandling.maxEndpointingDelay',
          'assistantsPage.modals.turnHandling.maxEndpointingDelay',
        )}
        {selectField(
          'turnHandling.endpointingMode',
          'assistantsPage.modals.turnHandling.endpointingMode',
          ['dynamic', 'fixed'],
        )}
        {selectField(
          'turnHandling.interruptionMode',
          'assistantsPage.modals.turnHandling.interruptionMode',
          ['vad', 'adaptive'],
        )}
        {numberField(
          'turnHandling.minInterruptionWords',
          'assistantsPage.modals.turnHandling.minInterruptionWords',
          '1',
        )}
        {numberField(
          'turnHandling.minInterruptionDuration',
          'assistantsPage.modals.turnHandling.minInterruptionDuration',
        )}
        {numberField(
          'turnHandling.falseInterruptionTimeout',
          'assistantsPage.modals.turnHandling.falseInterruptionTimeout',
        )}
      </Box>

      <Box sx={{ mt: 1 }}>
        {switchField(
          'turnHandling.interruptionEnabled',
          'assistantsPage.modals.turnHandling.interruptionEnabled',
        )}
        {switchField(
          'turnHandling.resumeFalseInterruption',
          'assistantsPage.modals.turnHandling.resumeFalseInterruption',
        )}
      </Box>

      <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>
        {t('assistantsPage.modals.turnHandling.preemptiveGeneration.title')}
      </Typography>
      <Box>
        {switchField(
          'turnHandling.preemptiveGeneration.enabled',
          'assistantsPage.modals.turnHandling.preemptiveGeneration.enabled',
        )}
        {switchField(
          'turnHandling.preemptiveGeneration.preemptiveTts',
          'assistantsPage.modals.turnHandling.preemptiveGeneration.preemptiveTts',
        )}
      </Box>
      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mt: 1 }}>
        {numberField(
          'turnHandling.preemptiveGeneration.maxSpeechDuration',
          'assistantsPage.modals.turnHandling.preemptiveGeneration.maxSpeechDuration',
        )}
        {numberField(
          'turnHandling.preemptiveGeneration.maxRetries',
          'assistantsPage.modals.turnHandling.preemptiveGeneration.maxRetries',
          '1',
        )}
      </Box>
    </>
  );

  if (!collapsible) {
    return <Box>{body}</Box>;
  }

  return (
    <Accordion disableGutters>
      <AccordionSummary expandIcon={<IconChevronDown />}>
        <Typography variant="subtitle2">{t('assistantsPage.modals.turnHandling.title')}</Typography>
      </AccordionSummary>
      <AccordionDetails>{body}</AccordionDetails>
    </Accordion>
  );
};
